import { ApolloError, useApolloClient, useMutation } from '@apollo/client'
import {
  Dialog,
  DialogMaxWidth,
  SnackbarVariant,
  UseSnackbar,
  useSnackbar
} from '@focaldata/cin-ui-components'
import React, { Dispatch, SetStateAction, useState } from 'react'
import { AddMarketCacheUpdate } from '../../../apollo/cacheModify/Project/addMarket'
import { CREATE_SURVEY } from '../../../containers/Page/MultiMarket/ProjectPage/ProjectPage.mutations'
import {
  getMarkets,
  surveysByCreation
} from '../../../containers/Page/MultiMarket/markets.utils'
import {
  DeleteProjectMutationVariables,
  Project,
  ProjectType,
  WavesQueryVariables
} from '../../../data/gql-gen/dashboard/graphql'
import { LoggerErrorType } from '../../../data/logger'
import { useNavigateToInitialProjectPage } from '../../../hooks/project/useNavigateToInitialProjectPage'
import useGetUserAndAccount from '../../../hooks/useGetUserAndAccount'
import { captureApolloError } from '../../../utils/HelperFunctions'
import UserSession from '../../../utils/UserSession'
import { isCountryCodedMarketName } from '../../../utils/marketFlags'
import {
  CREATE_PROJECT,
  CREATE_WAVE,
  DELETE_PROJECT,
  WaveMutationData
} from '../Project.mutations'
import { SEARCH_PROJECTS_QUERY_NAME, WAVES, WavesData } from '../Project.query'
import {
  DUPLICATE_MULTI_MARKET_QUESTIONNAIRE_V2,
  DUPLICATE_QUESTIONNAIRE_V2
} from '../Questionnaire.mutations'
import DialogDuplicateProject from './DialogDuplicateProject'

interface Props {
  isDialogDuplicateOpen: boolean
  setIsDialogDuplicateOpen: Dispatch<SetStateAction<boolean>>
  selectedProject: Project | undefined
}

const DuplicateProject: React.FC<Props> = (props: Props) => {
  const { isDialogDuplicateOpen, setIsDialogDuplicateOpen, selectedProject } =
    props

  const {
    userName,
    loading: loadingGetUserAndAccount,
    markup
  } = useGetUserAndAccount()

  const { enqueueSnackbar }: UseSnackbar = useSnackbar()
  const navigateToInitialProjectPage = useNavigateToInitialProjectPage()
  const client = useApolloClient()
  const [isDuplicating, setIsDuplicating] = useState<boolean>(false)

  const toggleDuplicateDialog = () => {
    setIsDialogDuplicateOpen(!isDialogDuplicateOpen)
  }

  const [createProject] = useMutation(CREATE_PROJECT, {
    context: { clientName: 'dashboard' },
    onError: (error) => {
      captureApolloError(LoggerErrorType.ApolloMutation, 'createProject', error)
    }
  })

  const [createSurvey] = useMutation(CREATE_SURVEY, {
    context: { clientName: 'dashboard' },
    onError: (error) => {
      captureApolloError(LoggerErrorType.ApolloMutation, 'createSurvey', error)
    }
  })

  const [createWave] = useMutation<WaveMutationData, { projectId: string }>(
    CREATE_WAVE,
    {
      context: { clientName: 'dashboard' },
      onError: (error) => {
        captureApolloError(LoggerErrorType.ApolloMutation, 'createWave', error)
      }
    }
  )

  // A tracker is copied from its latest wave; the backend orders waves by number.
  const getLatestWave = async (projectId: string) => {
    try {
      const { data } = await client.query<WavesData, WavesQueryVariables>({
        query: WAVES,
        variables: { projectId },
        context: { clientName: 'dashboard' },
        fetchPolicy: 'network-only'
      })
      return data.waves?.at(-1)
    } catch (error) {
      if (error instanceof ApolloError) {
        captureApolloError(LoggerErrorType.ApolloQuery, 'waves', error)
      }
      throw error
    }
  }

  // Legacy single-survey duplication — used for every non-multi-market type.
  const [duplicateQuestionnaire] = useMutation(DUPLICATE_QUESTIONNAIRE_V2, {
    context: { clientName: 'questionnaire' },
    refetchQueries: [SEARCH_PROJECTS_QUERY_NAME],
    onError: (error) => {
      captureApolloError(
        LoggerErrorType.ApolloMutation,
        'duplicateQuestionnaire',
        error
      )
    }
  })

  // Multi-market duplication — used for MultiMarketV2 projects (complete mapping).
  const [duplicateMultiMarketQuestionnaire] = useMutation(
    DUPLICATE_MULTI_MARKET_QUESTIONNAIRE_V2,
    {
      context: { clientName: 'questionnaire' },
      refetchQueries: [SEARCH_PROJECTS_QUERY_NAME],
      onError: (error) => {
        captureApolloError(
          LoggerErrorType.ApolloMutation,
          'duplicateMultiMarketQuestionnaire',
          error
        )
      }
    }
  )

  const [deleteProject] = useMutation<void, DeleteProjectMutationVariables>(
    DELETE_PROJECT,
    {
      context: { clientName: 'dashboard' },
      onError: (error) => {
        captureApolloError(
          LoggerErrorType.ApolloMutation,
          'deleteProject',
          error
        )
      }
    }
  )

  const handleDuplicateProject = async (project: Project) => {
    // Guard against a double-submit while duplication is in flight.
    if (isDuplicating) {
      return
    }
    setIsDuplicating(true)

    // Track the new project id so a mid-flow failure can roll it back instead
    // of stranding an orphan "Copy of ..." project.
    let createdProjectId: string | undefined

    try {
      const isTracker = project.projectType === ProjectType.Tracker
      const sourceWave = isTracker
        ? await getLatestWave(project.projectId)
        : undefined
      if (sourceWave?.surveys.length === 0) {
        throw new Error('The latest wave has no markets to copy.')
      }
      const sourceMarkets = sourceWave
        ? surveysByCreation(sourceWave.surveys)
        : getMarkets(project)
      const sourceMaster = sourceMarkets.at(0)

      // 1. Create the new (empty) project. It always starts with exactly one
      //    survey — its master. A market-named source survey is carried over so
      //    the copy keeps its country code and audience label instead of taking
      //    the "Copy of ..." project name.
      const { data: createProjectData } = await createProject({
        variables: {
          name: `Copy of ${project.name}`,
          description: project.description,
          accountId: UserSession.getAccountId(),
          solutionId: UserSession.getSolutionId(),
          creator: userName || 'N/A',
          projectType: project.projectType,
          surveyName: isCountryCodedMarketName(sourceMaster?.name)
            ? sourceMaster?.name
            : undefined
        }
      })

      const createdProject = createProjectData?.createProject
      const newProjectId = createdProject?.projectId
      const newMasterSurveyId = createdProject?.surveys[0]?.surveyId

      if (
        !createdProject ||
        !newProjectId ||
        !newMasterSurveyId ||
        !sourceMaster
      ) {
        throw new Error('Duplication failed: missing new project or survey id.')
      }

      // A "Copy of ..." project now exists — record it so the catch can delete
      // it if any later step fails.
      createdProjectId = newProjectId

      // A useMutation with an onError handler resolves (rather than rejects) on
      // failure, so the result of each duplication is inspected below to route
      // errors into the catch.
      if (project.projectType === ProjectType.MultiMarketV2 || sourceWave) {
        // 2a. Multi-market, or a tracker's latest wave: re-create every
        //     non-master market on the new project (survey only — the backend
        //     copies each market's fieldwork + audience during duplication, so we
        //     must NOT call createFieldworkV2). Preserving the source name
        //     carries its "(XX)" country suffix over. Then duplicate across the
        //     COMPLETE source -> new mapping (the backend hard-fails unless
        //     every source market is covered).
        const marketMappings = [
          {
            sourceSurveyId: sourceMaster.surveyId,
            newSurveyId: newMasterSurveyId
          }
        ]

        // A tracker's copy gets one wave, which adopts the master survey.
        let newWaveId: string | undefined
        if (sourceWave) {
          const { data: createWaveData } = await createWave({
            variables: { projectId: newProjectId }
          })
          newWaveId = createWaveData?.createWave?.waveId
          if (!newWaveId) {
            throw new Error('createWave returned no waveId')
          }
        }

        const nonMasterMarkets = sourceMarkets.filter(
          (survey) => survey.surveyId !== sourceMaster.surveyId
        )

        // Create the markets one at a time rather than with Promise.all: this
        // keeps the source order and, crucially, guarantees no createSurvey is
        // still in flight if a later step fails and the catch rolls the project
        // back via deleteProject (a concurrent create could otherwise land on a
        // project mid-deletion).
        for (const market of nonMasterMarkets) {
          const { data: createSurveyData } = await createSurvey({
            variables: {
              name: market.name,
              projectId: newProjectId,
              waveId: newWaveId
            },
            update: (cache, { data }) => {
              if (data?.createSurvey) {
                AddMarketCacheUpdate(cache, newProjectId, data.createSurvey)
              }
            }
          })
          const createdSurveyId = createSurveyData?.createSurvey?.surveyId
          if (!createdSurveyId) {
            throw new Error('createSurvey returned no surveyId')
          }
          marketMappings.push({
            sourceSurveyId: market.surveyId,
            newSurveyId: createdSurveyId
          })
        }

        const { data: duplicateData } = await duplicateMultiMarketQuestionnaire(
          {
            variables: {
              projectId: project.projectId,
              newProjectId,
              marketMappings,
              accountMarkup: markup,
              sourceWaveId: sourceWave?.waveId,
              newWaveId
            }
          }
        )
        if (!duplicateData?.duplicateMultiMarketQuestionnaireV2) {
          throw new Error(
            'duplicateMultiMarketQuestionnaireV2 returned no data'
          )
        }
      } else if (!isTracker) {
        // 2b. Single-market (AdHoc / FDChat / MRP): the source's one market
        //     collapses onto the new project's auto-created master survey. A
        //     tracker with no waves has nothing to copy.
        const { data: duplicateData } = await duplicateQuestionnaire({
          variables: {
            projectId: project.projectId,
            newProjectId,
            newSurveyId: newMasterSurveyId,
            accountMarkup: markup
          }
        })
        if (!duplicateData?.duplicateQuestionnaireV2) {
          throw new Error('duplicateQuestionnaireV2 returned no data')
        }
      }

      // 3. Done — close the dialog and open the copy.
      setIsDialogDuplicateOpen(false)
      navigateToInitialProjectPage(createdProject)
    } catch {
      // Per-mutation onError handlers already log to Datadog. Roll back the
      // half-created copy so a failure doesn't strand an orphan project, then
      // surface a single user-facing error.
      if (createdProjectId) {
        await deleteProject({
          variables: {
            projectId: createdProjectId,
            accountId: UserSession.getAccountId()
          }
        })
      }
      enqueueSnackbar(
        'Something went wrong. Try again, if the error persists contact support@focaldata.com.',
        { variant: SnackbarVariant.Error }
      )
    } finally {
      setIsDuplicating(false)
    }
  }

  return (
    <Dialog
      open={isDialogDuplicateOpen}
      onClose={toggleDuplicateDialog}
      primaryButtonClick={() => {
        return selectedProject && handleDuplicateProject(selectedProject)
      }}
      primaryButtonText="Duplicate project"
      primaryButtonDisabled={loadingGetUserAndAccount || isDuplicating}
      title="Duplicate project"
      maxWidth={DialogMaxWidth.Sm}
      onClickOutside={toggleDuplicateDialog}
    >
      <DialogDuplicateProject
        isTracker={selectedProject?.projectType === ProjectType.Tracker}
      />
    </Dialog>
  )
}

export default DuplicateProject
